<script setup lang="ts">
  import { computed } from 'vue'
  import {
    isStakeholderClient,
    isStakeholderVendor,
    testIds,
    type Client,
    type Vendor
  } from '@gat-ship/types'
  import { text } from '@gat-ship/stakeholders'
  import { IconLoading } from '@gat-ship/icons'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    Button
  } from '@gat-ship/vue-components'
  import { useStakeholderContext } from '../../../composables/useStakeholder'
  import { useStakeholderModals } from '../../providers/stakeholder-modals'
  import {
    useStakeholderEditAction,
    type StakeholderEditButtonConfig
  } from '../../../composables/useStakeholderEditAction'
  // fallow-ignore-next-line code-duplication
  const service = useStakeholderContext()
  const modals = useStakeholderModals()

  const stakeholder = computed(() => service?.stakeholder.value)

  const { isLoading, canShow, btnConfig } = useStakeholderEditAction(
    () => stakeholder.value,
    () =>
      text.groups.internalIdentificationNumbers(
        stakeholder.value?.stakeholderType ?? 'client'
      ).buttons.editInternalIdentificationNumbers as StakeholderEditButtonConfig
  )
</script>

<template>
  <ModalSection
    v-if="
      stakeholder &&
      (isStakeholderClient(stakeholder) || isStakeholderVendor(stakeholder))
    "
  >
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>
        {{
          text.groups.internalIdentificationNumbers(stakeholder.stakeholderType)
            .title
        }}
      </ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <div class="divide-y">
      <div
        v-if="isStakeholderVendor(stakeholder)"
        class="flex items-center justify-between gap-4 py-3"
      >
        <span class="text-sm font-medium">Vendor Number</span>
        <span
          class="text-muted-foreground text-sm"
          :data-testid="
            testIds.stakeholder.sections.internalIdentificationNumbers
              .vendorNumber
          "
          >{{ stakeholder.vendorNumber ?? '-' }}</span
        >
      </div>
      <div v-else class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Client Number</span>
        <span
          class="text-muted-foreground text-sm"
          :data-testid="
            testIds.stakeholder.sections.internalIdentificationNumbers
              .clientNumber
          "
          >{{ stakeholder.clientNumber ?? '-' }}</span
        >
      </div>
    </div>
    <Button
      v-if="canShow"
      :variant="btnConfig.variant"
      :size="btnConfig.size"
      :class="btnConfig.class"
      :disabled="isLoading"
      :data-testid="
        testIds.stakeholders.actions.changeInternalIdentificationNumbers
      "
      @click="
        modals?.showUpdateInternalIdentificationNumbers({
          stakeholder: stakeholder as Client | Vendor
        })
      "
    >
      <IconLoading v-if="isLoading" class="size-4 shrink-0" />
      <component
        :is="btnConfig.icon"
        v-else-if="btnConfig.icon"
        class="size-4"
      />
      {{ btnConfig.label }}
    </Button>
  </ModalSection>
</template>
